import React, { useState, useEffect } from "react";
import API from "../../../utils/api";
import UploadImg from "../../../utils/UploadImg";
import { toast } from "react-toastify";

const EditHeroModal = ({ hero, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    heading: "",
    description: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hero) {
      setForm({
        heading: hero.heading || "",
        description: hero.description || "",
      });
      setPreview(hero.image || null);
    }
  }, [hero]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let imageUrl = hero.image || "";

      // If new image selected → upload
      if (imageFile) {
        imageUrl = await UploadImg(imageFile);
      }

      // If removed → empty
      if (!preview) {
        imageUrl = "";
      }

      await API.put(`/hero/update-hero/${hero._id}`, {
        ...form,
        image: imageUrl,
      });

      onSuccess();
      toast.success("Hero Section updated successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
      toast.error(
        err.response?.data?.message ||
          "Unable to update Hero Section. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-100 to-pink-100 border-b">
          <h2 className="text-xl font-bold text-gray-800">Edit Hero Section</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hero Image
            </label>

            <div
              onClick={() => document.getElementById("edit-hero-image").click()}
              className="border-2 border-dashed border-blue-300 hover:border-pink-400 rounded-xl p-4 cursor-pointer transition"
            >
              {preview ? (
                <div className="relative w-fit">
                  <img
                    src={preview}
                    alt=""
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage();
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-pink-500 text-white rounded-full text-xs flex items-center justify-center"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-6">
                  <p className="text-3xl">🖼️</p>
                  <p className="text-sm mt-1">Click to upload image</p>
                </div>
              )}
            </div>

            <input
              id="edit-hero-image"
              type="file"
              accept="image/*"
              onChange={handleImage}
              className="hidden"
            />
          </div>

          {/* Heading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heading <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="heading"
              value={form.heading}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-pink-400"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-pink-500 hover:opacity-90 text-white font-medium disabled:opacity-60"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditHeroModal;
